import { Table } from 'antd'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { PerModal } from '../../../components/custom_components/Modal'
import PerText from '../../../components/custom_components/Text'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { IconNames } from '../../../components/util/types'
import { WorkforceMovementPeriodEnum } from '../../../types'
import {
  useCompanyManagerDashboardWorkforceLeavers,
  useCompanyManagerDashboardWorkforceNewEntries,
  useCompanyManagerDashboardWorkforceProbationPeriodEnding,
} from '../../../graphql/company-manager-dashboard/hooks/useCompanyManagerDashboardWorkforceQueries'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerIcon from '../../../components/custom_components/Icon'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { PerAvatarPlus } from '../../../components/custom_components/AvatarPlus'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { cancellationTypeToLabel, contractTypeEnumToLabel } from '../../../shared/contractEnumLabels'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import { useDashboardStore } from '../DashboardStore'

export type DashboardWorkforceMovementKind = 'newEntries' | 'employeeExits' | 'probationEnd'

export type DashboardWorkforceMovementModalHeaderProps = {
  icon: IconNames
  mainValue: number
  label: string
}

export type DashboardWorkforceMovementModalSelectionSingle = {
  view: 'single'
  kind: DashboardWorkforceMovementKind
  period: WorkforceMovementPeriodEnum
  monthLabel: string
  subCounterValue: number
  header: DashboardWorkforceMovementModalHeaderProps
}

export type DashboardWorkforceMovementModalSelectionSplit = {
  view: 'split'
  kind: DashboardWorkforceMovementKind
  firstPeriodLabel: string
  secondPeriodLabel: string
  firstSubValue: number
  secondSubValue: number
  header: DashboardWorkforceMovementModalHeaderProps
}

export type DashboardWorkforceMovementModalSelection =
  | DashboardWorkforceMovementModalSelectionSingle
  | DashboardWorkforceMovementModalSelectionSplit

type DashboardWorkforceMovementModalProps = {
  open: boolean
  onClose: () => void
  companyId: string
  selection: DashboardWorkforceMovementModalSelection | null
}

type WorkforceNewEntryRow = {
  id: number
  firstName: string
  lastName: string
  currentLevel: string | null
  replyEntryDate: string
  contractValidFrom: string
  businessUnit: {
    name: string
  }
}

type WorkforceProbationRow = {
  id: number
  firstName: string
  lastName: string
  currentLevel?: string | null
  probationPeriodEndDate: string
  businessUnit: {
    name: string
  }
}

type WorkforceLeaverRow = {
  id: number
  firstName: string
  lastName: string
  leaverLevel?: string | null
  contractCancellationTo: string
  contractCancellationType: string
  contractType: string
  businessUnit: {
    name: string
  }
}

const getWorkforceModalEmptyState = (t: TFunction, kind: DashboardWorkforceMovementKind, period: WorkforceMovementPeriodEnum) => {
  if (kind === 'newEntries') {
    if (period === WorkforceMovementPeriodEnum.PreviousMonth) {
      return {
        info: t('dashboard.companyManager.header.workforceModal.emptyState.newEntries.previousMonth.info'),
      }
    }
    return {
      info: t('dashboard.companyManager.header.workforceModal.emptyState.newEntries.currentMonth.info'),
    }
  }
  if (kind === 'employeeExits') {
    if (period === WorkforceMovementPeriodEnum.NextMonth) {
      return {
        info: t('dashboard.companyManager.header.workforceModal.emptyState.employeeExits.nextMonth.info'),
      }
    }
    return {
      info: t('dashboard.companyManager.header.workforceModal.emptyState.employeeExits.currentMonth.info'),
    }
  }
  if (period === WorkforceMovementPeriodEnum.NextMonth) {
    return {
      info: t('dashboard.companyManager.header.workforceModal.emptyState.probationEnd.nextMonth.info'),
    }
  }
  return {
    info: t('dashboard.companyManager.header.workforceModal.emptyState.probationEnd.currentMonth.info'),
  }
}

const getSplitPeriodPair = (kind: DashboardWorkforceMovementKind): [WorkforceMovementPeriodEnum, WorkforceMovementPeriodEnum] => {
  if (kind === 'newEntries') {
    return [WorkforceMovementPeriodEnum.PreviousMonth, WorkforceMovementPeriodEnum.CurrentMonth]
  }
  return [WorkforceMovementPeriodEnum.CurrentMonth, WorkforceMovementPeriodEnum.NextMonth]
}

const getWorkforceModalSplitAllEmpty = (t: TFunction, kind: DashboardWorkforceMovementKind) => {
  if (kind === 'newEntries') {
    return {
      heading: t('dashboard.companyManager.header.workforceModal.emptyState.splitView.allZero.newEntries.heading'),
      info: t('dashboard.companyManager.header.workforceModal.emptyState.splitView.allZero.newEntries.info'),
    }
  }
  if (kind === 'employeeExits') {
    return {
      heading: t('dashboard.companyManager.header.workforceModal.emptyState.splitView.allZero.employeeExits.heading'),
      info: t('dashboard.companyManager.header.workforceModal.emptyState.splitView.allZero.employeeExits.info'),
    }
  }
  return {
    heading: t('dashboard.companyManager.header.workforceModal.emptyState.splitView.allZero.probationEnd.heading'),
    info: t('dashboard.companyManager.header.workforceModal.emptyState.splitView.allZero.probationEnd.info'),
  }
}

const WorkforceNewEntriesTable: FC<{
  monthLabel: string
  rows: WorkforceNewEntryRow[]
}> = ({ monthLabel, rows }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const columns = useMemo(
    () => [
      {
        key: 'employee',
        title: t('employeeList.table.employee'),
        className: 'textAlign-left',
        width: '30%',
        sorter: (a: WorkforceNewEntryRow, b: WorkforceNewEntryRow) => {
          const nameA = computeFullname(user.nameDisplayPreference as NameDisplayPreference, a.firstName, a.lastName)
          const nameB = computeFullname(user.nameDisplayPreference as NameDisplayPreference, b.firstName, b.lastName)
          return nameA.localeCompare(nameB)
        },
        render: (row: WorkforceNewEntryRow) => (
          <PerAvatarPlus
            nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
            employeeId={row.id}
            firstName={row.firstName}
            lastName={row.lastName}
          />
        ),
      },
      {
        key: 'level',
        title: t('employeeList.table.level'),
        className: 'textAlign-left',
        width: '10%',
        render: (row: WorkforceNewEntryRow) => (row.currentLevel ? <PerText>{row.currentLevel}</PerText> : <PerText>-</PerText>),
      },
      {
        key: 'businessUnit',
        title: t('employeeList.table.bu'),
        className: 'textAlign-left',
        width: '15%',
        render: (row: WorkforceNewEntryRow) => <PerText>{row.businessUnit.name}</PerText>,
      },
      {
        key: 'replyEntryDate',
        title: t('dashboard.companyManager.header.workforceModal.replyEntryDate'),
        className: ' textAlign-left',
        width: '20%',
        sorter: (a: WorkforceNewEntryRow, b: WorkforceNewEntryRow) => a.replyEntryDate.localeCompare(b.replyEntryDate),
        render: (row: WorkforceNewEntryRow) => <PerText color="secondary">{formatDateShort(row.replyEntryDate)}</PerText>,
      },
      {
        key: 'contractValidFrom',
        title: t('dashboard.companyManager.header.workforceModal.validFrom'),
        className: 'textAlign-left',
        width: '25%',
        sorter: (a: WorkforceNewEntryRow, b: WorkforceNewEntryRow) => a.contractValidFrom.localeCompare(b.contractValidFrom),
        render: (row: WorkforceNewEntryRow) => <PerText color="secondary">{formatDateShort(row.contractValidFrom)}</PerText>,
      },
    ],
    [formatDateShort, t, user.nameDisplayPreference],
  )
  return (
    <>
      <PerText fontWeight="bold" fontSize="m" mb="s">
        {monthLabel}
      </PerText>
      <Table className="fullWidth small" dataSource={rows.map((row) => ({ ...row, key: row.id }))} columns={columns} pagination={false} />
    </>
  )
}

const WorkforceLeaversTable: FC<{
  monthLabel: string
  rows: WorkforceLeaverRow[]
}> = ({ monthLabel, rows }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const columns = useMemo(
    () => [
      {
        key: 'employee',
        title: t('employeeList.table.employee'),
        className: 'textAlign-left',
        width: '26%',
        sorter: (a: WorkforceLeaverRow, b: WorkforceLeaverRow) => {
          const nameA = computeFullname(user.nameDisplayPreference as NameDisplayPreference, a.firstName, a.lastName)
          const nameB = computeFullname(user.nameDisplayPreference as NameDisplayPreference, b.firstName, b.lastName)
          return nameA.localeCompare(nameB)
        },
        render: (row: WorkforceLeaverRow) => (
          <PerAvatarPlus
            nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
            employeeId={row.id}
            firstName={row.firstName}
            lastName={row.lastName}
          />
        ),
      },
      {
        key: 'level',
        title: t('employeeList.table.level'),
        className: 'textAlign-left',
        width: '10%',
        render: (row: WorkforceLeaverRow) => (row.leaverLevel ? <PerText>{row.leaverLevel}</PerText> : <PerText>-</PerText>),
      },
      {
        key: 'businessUnit',
        title: t('employeeList.table.bu'),
        className: 'textAlign-left',
        width: '14%',
        render: (row: WorkforceLeaverRow) => <PerText>{row.businessUnit.name}</PerText>,
      },
      {
        key: 'contractType',
        title: t('dashboard.companyManager.header.workforceModal.contractType'),
        className: 'textAlign-left',
        width: '16%',
        render: (row: WorkforceLeaverRow) => <PerText>{contractTypeEnumToLabel(t, row.contractType)}</PerText>,
      },
      {
        key: 'contractCancellationTo',
        title: t('dashboard.companyManager.header.workforceModal.contractCancellationTo'),
        className: 'textAlign-left',
        width: '17%',
        sorter: (a: WorkforceLeaverRow, b: WorkforceLeaverRow) => a.contractCancellationTo.localeCompare(b.contractCancellationTo),
        render: (row: WorkforceLeaverRow) => <PerText color="secondary">{formatDateShort(row.contractCancellationTo)}</PerText>,
      },
      {
        key: 'contractCancellationType',
        title: t('dashboard.companyManager.header.workforceModal.contractCancellationType'),
        className: 'textAlign-left',
        width: '17%',
        render: (row: WorkforceLeaverRow) => <PerText>{cancellationTypeToLabel(t, row.contractCancellationType)}</PerText>,
      },
    ],
    [formatDateShort, t, user.nameDisplayPreference],
  )
  return (
    <>
      <PerText fontWeight="bold" fontSize="m" mb="s">
        {monthLabel}
      </PerText>
      <Table className="fullWidth small" dataSource={rows.map((row) => ({ ...row, key: row.id }))} columns={columns} pagination={false} />
    </>
  )
}

const WorkforceProbationTable: FC<{
  monthLabel: string
  rows: WorkforceProbationRow[]
}> = ({ monthLabel, rows }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const columns = useMemo(
    () => [
      {
        key: 'employee',
        title: t('employeeList.table.employee'),
        className: 'textAlign-left',
        width: '35%',
        sorter: (a: WorkforceProbationRow, b: WorkforceProbationRow) => {
          const nameA = computeFullname(user.nameDisplayPreference as NameDisplayPreference, a.firstName, a.lastName)
          const nameB = computeFullname(user.nameDisplayPreference as NameDisplayPreference, b.firstName, b.lastName)
          return nameA.localeCompare(nameB)
        },
        render: (row: WorkforceProbationRow) => (
          <PerAvatarPlus
            nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
            employeeId={row.id}
            firstName={row.firstName}
            lastName={row.lastName}
          />
        ),
      },
      {
        key: 'level',
        title: t('employeeList.table.level'),
        className: 'textAlign-left',
        width: '15%',
        render: (row: WorkforceProbationRow) => (row.currentLevel ? <PerText>{row.currentLevel}</PerText> : <PerText>-</PerText>),
      },
      {
        key: 'businessUnit',
        title: t('employeeList.table.bu'),
        className: 'textAlign-left',
        width: '20%',
        render: (row: WorkforceProbationRow) => <PerText>{row.businessUnit.name}</PerText>,
      },
      {
        key: 'probationPeriodEndDate',
        title: t('dashboard.companyManager.header.workforceModal.probationPeriodEndDate'),
        className: 'textAlign-left',
        width: '30%',
        sorter: (a: WorkforceProbationRow, b: WorkforceProbationRow) => a.probationPeriodEndDate.localeCompare(b.probationPeriodEndDate),
        render: (row: WorkforceProbationRow) => <PerText color="secondary">{formatDateShort(row.probationPeriodEndDate)}</PerText>,
      },
    ],
    [formatDateShort, t, user.nameDisplayPreference],
  )
  return (
    <>
      <PerText fontWeight="bold" fontSize="m" mb="s">
        {monthLabel}
      </PerText>
      <Table className="fullWidth small" dataSource={rows.map((row) => ({ ...row, key: row.id }))} columns={columns} pagination={false} />
    </>
  )
}

const DashboardWorkforceMovementModalQueries: FC<{
  companyId: string
  kind: DashboardWorkforceMovementKind
  period: WorkforceMovementPeriodEnum
  monthLabel: string
  subCounterValue: number
  t: TFunction
}> = ({ companyId, kind, period, monthLabel, subCounterValue, t }) => {
  const userDashboardRoles = useDashboardStore((state) => state.selectedDashboardRoles)
  const skipDetailQuery = subCounterValue === 0
  const newEntries = useCompanyManagerDashboardWorkforceNewEntries(companyId, period, userDashboardRoles, {
    skip: kind !== 'newEntries' || skipDetailQuery,
  })
  const leavers = useCompanyManagerDashboardWorkforceLeavers(companyId, period, userDashboardRoles, {
    skip: kind !== 'employeeExits' || skipDetailQuery,
  })
  const probation = useCompanyManagerDashboardWorkforceProbationPeriodEnding(companyId, period, userDashboardRoles, {
    skip: kind !== 'probationEnd' || skipDetailQuery,
  })

  if (subCounterValue === 0) {
    const { info } = getWorkforceModalEmptyState(t, kind, period)
    return (
      <>
        <PerText fontWeight="bold" fontSize="m" mb="s">
          {monthLabel}
        </PerText>
        <BackgroundBox color="gray-2" noShadow r>
          <PerText color="secondary" fontSize="s">
            {info}
          </PerText>
        </BackgroundBox>
      </>
    )
  }

  const { data, loading, error } = kind === 'newEntries' ? newEntries : kind === 'employeeExits' ? leavers : probation

  if (loading || error || !data) {
    return (
      <PerFlexRow my="xs" width="fullWidth" justify="center">
        <NewLoadingAndErrors isLoading={loading} hasNoData={!data} error={error} showSpinner t={t} />
      </PerFlexRow>
    )
  }

  if (kind === 'newEntries') {
    const newEntriesByPeriod = newEntries.data!.companyManagerDashboardWorkforceNewEntries
    const rows = period === WorkforceMovementPeriodEnum.PreviousMonth ? newEntriesByPeriod.previousMonth : newEntriesByPeriod.currentMonth
    return <WorkforceNewEntriesTable monthLabel={monthLabel} rows={rows} />
  }
  if (kind === 'probationEnd') {
    const probationByPeriod = probation.data!.companyManagerDashboardWorkforceProbationPeriodEnding
    const rows = period === WorkforceMovementPeriodEnum.NextMonth ? probationByPeriod.nextMonth : probationByPeriod.currentMonth
    return <WorkforceProbationTable monthLabel={monthLabel} rows={rows} />
  }
  const leaversByPeriod = leavers.data!.companyManagerDashboardWorkforceLeavers
  const rows = period === WorkforceMovementPeriodEnum.NextMonth ? leaversByPeriod.nextMonth : leaversByPeriod.currentMonth
  return <WorkforceLeaversTable monthLabel={monthLabel} rows={rows} />
}

const DashboardWorkforceMovementModalSplitQueries: FC<{
  companyId: string
  selection: DashboardWorkforceMovementModalSelectionSplit
  t: TFunction
}> = ({ companyId, selection, t }) => {
  const userDashboardRoles = useDashboardStore((state) => state.selectedDashboardRoles)
  const { kind, firstPeriodLabel, secondPeriodLabel, firstSubValue, secondSubValue } = selection
  const [firstPeriod, secondPeriod] = getSplitPeriodPair(kind)
  const skipDetailQuery = firstSubValue === 0 && secondSubValue === 0

  const newEntries = useCompanyManagerDashboardWorkforceNewEntries(companyId, null, userDashboardRoles, {
    skip: kind !== 'newEntries' || skipDetailQuery,
  })
  const leavers = useCompanyManagerDashboardWorkforceLeavers(companyId, null, userDashboardRoles, {
    skip: kind !== 'employeeExits' || skipDetailQuery,
  })
  const probation = useCompanyManagerDashboardWorkforceProbationPeriodEnding(companyId, null, userDashboardRoles, {
    skip: kind !== 'probationEnd' || skipDetailQuery,
  })

  if (skipDetailQuery) {
    const { info } = getWorkforceModalSplitAllEmpty(t, kind)
    return (
      <BackgroundBox color="gray-2" noShadow r>
        <PerText fontSize="s" color="secondary">
          {info}
        </PerText>
      </BackgroundBox>
    )
  }

  const { data, loading, error } = kind === 'newEntries' ? newEntries : kind === 'employeeExits' ? leavers : probation

  if (loading || error || !data) {
    return (
      <PerFlexRow my="xs" width="fullWidth" justify="center">
        <NewLoadingAndErrors isLoading={loading} hasNoData={!data} error={error} showSpinner t={t} />
      </PerFlexRow>
    )
  }

  const renderFirstBlock = () => {
    if (firstSubValue === 0) {
      const { info } = getWorkforceModalEmptyState(t, kind, firstPeriod)
      return (
        <>
          <PerText fontWeight="bold" fontSize="m" mb="s">
            {firstPeriodLabel}
          </PerText>
          <BackgroundBox color="gray-2" noShadow r>
            <PerText color="secondary" fontSize="s">
              {info}
            </PerText>
          </BackgroundBox>
        </>
      )
    }
    if (kind === 'newEntries') {
      const rows = newEntries.data!.companyManagerDashboardWorkforceNewEntries.previousMonth
      return <WorkforceNewEntriesTable monthLabel={firstPeriodLabel} rows={rows} />
    }
    if (kind === 'employeeExits') {
      const rows = leavers.data!.companyManagerDashboardWorkforceLeavers.currentMonth
      return <WorkforceLeaversTable monthLabel={firstPeriodLabel} rows={rows} />
    }
    const rows = probation.data!.companyManagerDashboardWorkforceProbationPeriodEnding.currentMonth
    return <WorkforceProbationTable monthLabel={firstPeriodLabel} rows={rows} />
  }

  const renderSecondBlock = () => {
    if (secondSubValue === 0) {
      const { info } = getWorkforceModalEmptyState(t, kind, secondPeriod)
      return (
        <>
          <PerText fontWeight="bold" fontSize="m" mb="s">
            {secondPeriodLabel}
          </PerText>
          <BackgroundBox color="gray-2" noShadow r>
            <PerText color="secondary" fontSize="s">
              {info}
            </PerText>
          </BackgroundBox>
        </>
      )
    }
    if (kind === 'newEntries') {
      const rows = newEntries.data!.companyManagerDashboardWorkforceNewEntries.currentMonth
      return <WorkforceNewEntriesTable monthLabel={secondPeriodLabel} rows={rows} />
    }
    if (kind === 'employeeExits') {
      const rows = leavers.data!.companyManagerDashboardWorkforceLeavers.nextMonth
      return <WorkforceLeaversTable monthLabel={secondPeriodLabel} rows={rows} />
    }
    const rows = probation.data!.companyManagerDashboardWorkforceProbationPeriodEnding.nextMonth
    return <WorkforceProbationTable monthLabel={secondPeriodLabel} rows={rows} />
  }

  return (
    <>
      {renderFirstBlock()}
      <PerFlexRow direction="column" width="fullWidth" mt="m">
        {renderSecondBlock()}
      </PerFlexRow>
    </>
  )
}

export const DashboardWorkforceMovementModal: FC<DashboardWorkforceMovementModalProps> = ({ open, onClose, companyId, selection }) => {
  const { t } = useTranslation()
  const icon = selection?.header.icon
  const totalValue = selection?.header.mainValue
  const label = selection?.header.label
  return (
    <PerModal
      visible={open}
      onCancel={onClose}
      size="large"
      title={
        open && selection
          ? {
              custom: (
                <PerFlexRow justify="start" align="middle" noWrap width="fullWidth">
                  <PerIcon name={icon!} size={32} color="primary" />

                  <PerText fontSize={'xl'} fontWeight="bold" mr="xxs" ml="xxs" color="default">
                    {totalValue}
                  </PerText>

                  <PerText transform="uppercase" color="default" fontSize="l">
                    {label}
                  </PerText>
                </PerFlexRow>
              ),
            }
          : { heading: '' }
      }
      footer={{ cancelBtn: { onClick: onClose, text: t('global.button.close') } }}
    >
      {open &&
        selection &&
        (selection.view === 'split' ? (
          <DashboardWorkforceMovementModalSplitQueries companyId={companyId} selection={selection} t={t} />
        ) : (
          <DashboardWorkforceMovementModalQueries
            companyId={companyId}
            kind={selection.kind}
            period={selection.period}
            monthLabel={selection.monthLabel}
            subCounterValue={selection.subCounterValue}
            t={t}
          />
        ))}
    </PerModal>
  )
}
